import {forwardRef, memo, useCallback, useImperativeHandle, useState} from "react";
import type {ICountry, PhoneInputProps} from "react-native-international-phone-number";
import {getCountryByPhoneNumber} from "react-native-international-phone-number";

import {Input, InputRightAddon} from "../ui/input";

import ReactNativeInternationalPhoneNumber from "./ReactNativeInternationalPhoneNumber";

type Props = {
    testID?: string;
    defaultValue?: string;
    country?: ICountry["cca2"];
    onlyCountries?: ICountry["cca2"][];
    showAddNew?: boolean;
    isEditing?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    onChange?: (value: string, inputValue: string) => void;
    onClickAddNew?: () => void;
    onDone?: () => void;
} & Pick<PhoneInputProps, "onKeyPress">;

type PhoneInputRef = {
    setInputValue: (value: string) => void;
};

const PhoneInput = forwardRef<PhoneInputRef, Props>(
    (
        {
            testID,
            defaultValue,
            country: initCountryCCA2 = "US",
            onlyCountries,
            showAddNew,
            isEditing,
            isDisabled,
            placeholder = "555-555-5555",
            onChange,
            onClickAddNew,
            onKeyPress,
            onDone
        },
        ref
    ) => {
        // defaultValue is used to init country and input value at first render
        const [value] = useState(() => {
            if (!defaultValue) return "";
            return defaultValue.startsWith("+") ? defaultValue : `+${defaultValue}`;
        });
        const [selectedCountry, setSelectedCountry] = useState<ICountry>();
        const [inputValue, setInputValue] = useState<string>("");

        const handleSetInputValue = useCallback(
            (value: string) => {
                if (value) {
                    const country = getCountryByPhoneNumber(value);
                    if (country) {
                        setInputValue(value.replace(country.idd.root, ""));
                        if (!selectedCountry || selectedCountry.idd.root !== country.idd.root) {
                            setSelectedCountry(country);
                        }
                    }
                } else {
                    setInputValue("");
                }
            },
            [selectedCountry]
        );

        const handleChangePhoneNumber = useCallback(
            (value: string) => {
                setInputValue(value);
                if (selectedCountry) {
                    onChange?.(
                        `${selectedCountry.idd.root.toString()}${value.toString()}`.replaceAll(
                            " ",
                            ""
                        ),
                        value
                    );
                }
            },
            [onChange, selectedCountry]
        );

        useImperativeHandle(
            ref,
            () => ({
                setInputValue: handleSetInputValue
            }),
            [handleSetInputValue]
        );

        return (
            <Input isDisabled={isDisabled}>
                <ReactNativeInternationalPhoneNumber
                    defaultCountry={initCountryCCA2}
                    // set defaultValue help init country and input value at first render
                    defaultValue={value}
                    disabled={isDisabled}
                    placeholder={placeholder}
                    selectedCountry={selectedCountry}
                    testID={testID}
                    value={inputValue}
                    visibleCountries={onlyCountries}
                    onChangePhoneNumber={handleChangePhoneNumber}
                    onChangeSelectedCountry={setSelectedCountry}
                    onKeyPress={onKeyPress}
                />
                {showAddNew && <InputRightAddon onPress={onClickAddNew}>+ Add</InputRightAddon>}
                {isEditing && <InputRightAddon onPress={onDone}>Done</InputRightAddon>}
            </Input>
        );
    }
);

PhoneInput.displayName = "PhoneInput";

export default memo(PhoneInput);
